import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, CreditCard, Smartphone, Banknote, ShieldCheck } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { saveRecentOrder } from "../lib/orderStorage";
import { toast } from "sonner";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { items, subtotal, mrpTotal, clearCart } = useCart();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", addr1: "", addr2: "", city: "", state: "", pin: "",
  });
  const [method, setMethod] = useState("razorpay");
  const [placing, setPlacing] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState(null); // { code, discount, label, discount_type }
  const [applying, setApplying] = useState(false);
  const [loyaltyBalance, setLoyaltyBalance] = useState(null);
  const [pointsToUse, setPointsToUse] = useState("");
  const [pointsApplied, setPointsApplied] = useState(null); // { points, discount }
  const [refInput, setRefInput] = useState("");
  const [refValid, setRefValid] = useState(null); // { code, bonus }
  const navigate = useNavigate();

  const savings = mrpTotal - subtotal;
  const couponDiscount = applied?.discount || 0;
  const pointsDiscount = pointsApplied?.discount || 0;
  const finalTotal = Math.max(0, subtotal - couponDiscount - pointsDiscount);

  // Lookup loyalty balance whenever email changes
  useEffect(() => {
    const email = form.email?.trim();
    if (!email || !email.includes("@")) { setLoyaltyBalance(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/loyalty/${encodeURIComponent(email)}`);
        setLoyaltyBalance(data);
      } catch { setLoyaltyBalance(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.email]);

  if (items.length === 0) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-black">No items to checkout</h1>
        <Link to="/shop" className="mt-4 inline-block text-[#FF5A1F] font-semibold">Continue shopping</Link>
      </main>
    );
  }

  const validate = () => {
    const required = ["name", "email", "phone", "addr1", "city", "state", "pin"];
    for (const k of required) {
      if (!form[k]?.trim()) {
        toast.error("Please fill all required fields");
        return false;
      }
    }
    return true;
  };

  const handleCOD = async () => {
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/payments/create-order`, {
        amount: finalTotal,
        currency: "INR",
        items: items.map((i) => ({ id: i.id, title: i.title, qty: i.qty, sale: i.sale })),
        customer: { ...form, coupon_code: applied?.code, coupon_discount: couponDiscount, points_used: pointsApplied?.points || 0, points_discount: pointsDiscount },
      });
      toast.success("Order placed!", { description: "Pay cash when your order arrives." });
      const orderResp = await axios.get(`${API}/orders/${data.id}`);
      saveRecentOrder(orderResp.data);
      clearCart();
      navigate(`/order/${data.id}`);
    } catch (e) {
      toast.error("Could not place order", { description: e?.response?.data?.detail || e.message });
    } finally {
      setPlacing(false);
    }
  };

  const handleRazorpay = async () => {
    if (!window.Razorpay) {
      toast.error("Payment library failed to load. Please refresh and try again.");
      return;
    }
    setPlacing(true);
    try {
      const { data: order } = await axios.post(`${API}/payments/create-order`, {
        amount: finalTotal,
        currency: "INR",
        items: items.map((i) => ({ id: i.id, title: i.title, qty: i.qty, sale: i.sale })),
        customer: { ...form, coupon_code: applied?.code, coupon_discount: couponDiscount, points_used: pointsApplied?.points || 0, points_discount: pointsDiscount },
      });

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Stealodeal",
        description: `Order ${order.receipt}`,
        image: "https://m.media-amazon.com/images/I/51TkG18Sx4L._SL1100_.jpg",
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },
        notes: {
          address: `${form.addr1}, ${form.addr2}, ${form.city}, ${form.state} ${form.pin}`,
        },
        theme: { color: "#FF5A1F" },
        handler: async (response) => {
          try {
            const verify = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful!", { description: "Your order has been placed." });
            if (verify.data?.order) saveRecentOrder(verify.data.order);
            clearCart();
            navigate(`/order/${order.id}`);
          } catch (e) {
            toast.error("Payment verification failed", { description: e?.response?.data?.detail || e.message });
          } finally {
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPlacing(false);
            toast.message("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed", { description: resp.error?.description || "Please try again" });
        setPlacing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error("Could not start payment", { description: e?.response?.data?.detail || e.message });
      setPlacing(false);
    }
  };

  const onPlace = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (method === "cod") return handleCOD();
    return handleRazorpay();
  };

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-14" data-testid="checkout-page">
      <Link to="/cart" className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900">
        <ArrowLeft className="w-4 h-4" /> Back to bag
      </Link>
      <h1 className="mt-3 font-display text-3xl md:text-5xl font-black leading-tight">Checkout</h1>

      <form onSubmit={onPlace} className="grid lg:grid-cols-[1fr_420px] gap-8 lg:gap-10 mt-6 md:mt-10">
        <div className="space-y-8">
          <section>
            <h2 className="font-display text-xl font-black mb-4">Contact</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field testid="checkout-name" label="Full name" value={form.name} set={(v) => setForm({ ...form, name: v })} required />
              <Field testid="checkout-email" label="Email" type="email" value={form.email} set={(v) => setForm({ ...form, email: v })} required />
              <Field testid="checkout-phone" label="Phone" type="tel" value={form.phone} set={(v) => setForm({ ...form, phone: v })} required />
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-black mb-4">Shipping Address</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field testid="checkout-addr1" label="Address line 1" value={form.addr1} set={(v) => setForm({ ...form, addr1: v })} required full />
              <Field testid="checkout-addr2" label="Address line 2 (optional)" value={form.addr2} set={(v) => setForm({ ...form, addr2: v })} full />
              <Field testid="checkout-city" label="City" value={form.city} set={(v) => setForm({ ...form, city: v })} required />
              <Field testid="checkout-state" label="State" value={form.state} set={(v) => setForm({ ...form, state: v })} required />
              <Field testid="checkout-pin" label="PIN code" value={form.pin} set={(v) => setForm({ ...form, pin: v })} required />
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-black mb-4">Payment</h2>
            <div className="space-y-2.5">
              <MethodRow icon={<CreditCard className="w-4 h-4" />} value="razorpay" method={method} setMethod={setMethod} label="Razorpay — Cards, UPI, Netbanking, Wallets" sub="Secured by Razorpay · 100+ banks supported" />
              <MethodRow icon={<Smartphone className="w-4 h-4" />} value="upi" method={method} setMethod={setMethod} label="UPI (via Razorpay)" sub="GPay, PhonePe, Paytm & more" />
              <MethodRow icon={<Banknote className="w-4 h-4" />} value="cod" method={method} setMethod={setMethod} label="Cash on Delivery" sub="Pay when your order arrives" />
            </div>
            <div className="mt-4 text-xs text-neutral-500 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> All transactions are encrypted and secure.
            </div>
          </section>
        </div>

        <aside className="bg-neutral-50 rounded-3xl p-5 md:p-8 h-fit lg:sticky lg:top-28">
          <h3 className="font-display text-xl font-black">Order Summary</h3>
          <div className="mt-5 space-y-3 max-h-64 overflow-auto pr-1">
            {items.map((i) => (
              <div key={i.id} className="flex gap-3 items-center">
                <div className="w-14 h-14 rounded-xl bg-white flex-shrink-0 overflow-hidden">
                  <img src={i.image} alt={i.title} className="w-full h-full object-contain p-1.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 line-clamp-1">{i.title}</div>
                  <div className="text-xs text-neutral-500">Qty {i.qty}</div>
                </div>
                <div className="text-sm font-semibold">₹{i.sale * i.qty}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-neutral-300 mt-5 pt-5 space-y-2 text-sm">
            <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span>₹{mrpTotal}</span></div>
            <div className="flex justify-between text-[#FF5A1F]"><span>Discount</span><span>– ₹{savings}</span></div>
            {applied && couponDiscount > 0 && (
              <div className="flex justify-between text-green-700"><span>Coupon ({applied.code})</span><span>– ₹{couponDiscount}</span></div>
            )}
            {applied && couponDiscount === 0 && (
              <div className="flex justify-between text-green-700"><span>Coupon ({applied.code})</span><span>{applied.label}</span></div>
            )}
            {pointsApplied && (
              <div className="flex justify-between text-purple-700"><span>Rewards ({pointsApplied.points} pts)</span><span>– ₹{pointsApplied.discount}</span></div>
            )}
            <div className="flex justify-between text-neutral-600"><span>Shipping</span><span className="text-green-600 font-semibold">FREE</span></div>
            <div className="flex justify-between items-baseline pt-3 border-t border-neutral-300">
              <span className="font-bold">Total</span>
              <span className="font-display text-2xl font-black" data-testid="checkout-total">₹{finalTotal}</span>
            </div>
          </div>

          {/* Coupon code input */}
          <div className="mt-5">
            {!applied ? (
              <div className="flex gap-2">
                <input
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="Have a coupon code?"
                  className="flex-1 h-11 rounded-full border border-neutral-300 px-4 text-sm bg-white uppercase font-mono focus:outline-none focus:border-neutral-900"
                  data-testid="coupon-input"
                />
                <button
                  type="button"
                  disabled={applying || !couponInput.trim()}
                  onClick={async () => {
                    setApplying(true);
                    try {
                      const { data } = await axios.post(`${API}/coupons/apply`, { code: couponInput.trim(), amount: subtotal });
                      setApplied(data);
                      toast.success(`${data.label} applied!`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Invalid coupon");
                    } finally { setApplying(false); }
                  }}
                  className="h-11 px-5 rounded-full bg-neutral-900 text-white text-sm font-bold disabled:opacity-40"
                  data-testid="apply-coupon-btn"
                >
                  {applying ? "..." : "Apply"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-full px-4 h-11" data-testid="coupon-applied">
                <div className="text-xs">
                  <span className="font-bold text-green-800">{applied.code}</span>
                  <span className="text-green-700 ml-1.5">— {applied.label}</span>
                </div>
                <button type="button" onClick={() => { setApplied(null); setCouponInput(""); }} className="text-xs font-semibold text-neutral-600 hover:text-red-600">
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Loyalty points */}
          {loyaltyBalance && loyaltyBalance.balance >= loyaltyBalance.min_redeem_points && (
            <div className="mt-3 bg-purple-50 border border-purple-200 rounded-2xl p-3" data-testid="loyalty-box">
              <div className="text-xs text-purple-800 flex items-center justify-between">
                <span>💎 You have <b>{loyaltyBalance.balance}</b> points (₹{loyaltyBalance.balance * loyaltyBalance.redeem_value_per_point} value)</span>
              </div>
              {!pointsApplied ? (
                <div className="mt-2 flex gap-2">
                  <input type="number" value={pointsToUse} onChange={(e) => setPointsToUse(e.target.value)}
                    placeholder={`Min ${loyaltyBalance.min_redeem_points} pts`}
                    className="flex-1 h-10 rounded-full border border-purple-300 px-4 text-sm bg-white focus:outline-none focus:border-purple-600"
                    data-testid="points-input" />
                  <button type="button"
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/loyalty/redeem`, {
                          email: form.email, points: parseInt(pointsToUse) || 0, amount: subtotal - couponDiscount,
                        });
                        setPointsApplied(data);
                        toast.success(`${data.points} pts redeemed — ₹${data.discount} off!`);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Redemption failed"); }
                    }}
                    className="h-10 px-4 rounded-full bg-purple-700 text-white text-xs font-bold" data-testid="redeem-points-btn">
                    Redeem
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="font-bold text-purple-800">{pointsApplied.points} pts applied — ₹{pointsApplied.discount} off</span>
                  <button type="button" onClick={() => { setPointsApplied(null); setPointsToUse(""); }} className="font-semibold text-neutral-600 hover:text-red-600">Remove</button>
                </div>
              )}
            </div>
          )}

          {/* Referral code — only for brand-new customers */}
          {form.email && form.email.includes("@") && (!loyaltyBalance || loyaltyBalance.total_earned === 0) && (
            <div className="mt-3">
              {!refValid ? (
                <div className="flex gap-2">
                  <input value={refInput} onChange={(e) => setRefInput(e.target.value.toUpperCase())}
                    placeholder="Referral code (from a friend)"
                    className="flex-1 h-11 rounded-full border border-neutral-300 px-4 text-sm bg-white uppercase font-mono focus:outline-none focus:border-neutral-900"
                    data-testid="referral-input" />
                  <button type="button" disabled={!refInput.trim()}
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/referral/validate`, { code: refInput.trim(), email: form.email });
                        setRefValid(data);
                        toast.success(`Referral applied — you'll earn ${data.bonus} bonus points!`);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Invalid referral code"); }
                    }}
                    className="h-11 px-5 rounded-full bg-neutral-900 text-white text-sm font-bold disabled:opacity-40"
                    data-testid="apply-referral-btn">Apply</button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-full px-4 h-11" data-testid="referral-applied">
                  <div className="text-xs">
                    <span className="font-bold text-blue-800">Referral {refValid.code}</span>
                    <span className="text-blue-700 ml-1.5">— +{refValid.bonus} bonus pts</span>
                  </div>
                  <button type="button" onClick={() => { setRefValid(null); setRefInput(""); }} className="text-xs font-semibold text-neutral-600 hover:text-red-600">Remove</button>
                </div>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={placing}
            className="mt-6 w-full h-12 rounded-full bg-[#FF5A1F] hover:bg-[#e64a0f] disabled:opacity-60 text-white font-bold text-sm transition flex items-center justify-center gap-2"
            data-testid="place-order-btn"
          >
            {placing ? "Processing..." : method === "cod" ? "Place Order (COD)" : `Pay Securely ₹${finalTotal}`}
          </button>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-neutral-500">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" /> Powered by Razorpay · 100% secure
          </div>
        </aside>
      </form>
    </main>
  );
}

function Field({ label, type = "text", value, set, required, full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-600">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => set(e.target.value)}
        className="mt-2 w-full h-12 rounded-full border border-neutral-300 bg-white px-5 text-sm focus:outline-none focus:border-neutral-900"
        data-testid={testid}
      />
    </div>
  );
}

function MethodRow({ icon, value, method, setMethod, label, sub }) {
  const active = method === value;
  return (
    <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition ${active ? "border-neutral-900 bg-white" : "border-neutral-200 hover:border-neutral-400"}`} data-testid={`pay-method-${value}`}>
      <input type="radio" name="pay" value={value} checked={active} onChange={() => setMethod(value)} className="accent-[#FF5A1F]" />
      <div className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">{icon}</div>
      <div className="flex-1">
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-neutral-500">{sub}</div>
      </div>
    </label>
  );
}
